import { MemberProfile } from "../model/members/MemberProfile";
import { MemberFollowup } from "../model/members/MemberFollowup";
import dayjs from "dayjs";

export interface MemberWithPrimaryFollowup {
  member: MemberProfile;
  primaryFollowup: MemberFollowup | null;
  allFollowups: MemberFollowup[];
}

/**
 * Classify member status from profile (Active / Inactive / Expired).
 */
export function getMemberStatus(member: MemberProfile): "Active" | "Inactive" | "Expired" {
  if (!member.isEnabled) return "Inactive";
  const now = dayjs();
  const validFrom = dayjs(member.validFrom);
  const validTo = dayjs(member.validTo);
  if (!validFrom.isValid() || !validTo.isValid()) return "Inactive";
  // Day-granularity comparison: validFrom/validTo are date-only values (revived
  // at midnight), and the rest of the UI (StatusBanner, MemberProfileScreenV2)
  // treats a member as active through the whole of their validTo day. Without
  // the "day" unit, a member would read as Expired from 00:00 on their final
  // day — pushing a just-converted member into Previous prematurely.
  if (now.isBefore(validFrom, "day")) return "Inactive";
  if (now.isAfter(validTo, "day")) return "Expired";
  return "Active";
}

/**
 * True if member is inactive or expired (previous member for retention).
 */
export function isPreviousMember(member: MemberProfile): boolean {
  return getMemberStatus(member) !== "Active";
}

export type RetentionTab =
  | "visitors"
  | "tours"
  | "calls"
  | "current"
  | "previous"
  | "legacy"
  | "other"
  | "deadleads";

function normaliseMetadata(value: unknown): string {
  return typeof value === "string" ? value.replace(/\s+/g, "") : value == null ? "" : String(value);
}

/**
 * Decide which retention tab a member belongs to.
 *
 * Tabs are primarily segmented by LeadSource (the lead's ORIGIN), but once a
 * prospect converts (LeadType === "Membership") they are a member, not a lead,
 * so they must leave the prospecting tabs (Visitors / Tours) and appear under
 * the member tabs. We only reroute prospect origins (Visitor / Tour / untagged)
 * — the CurrentMember / PreviousMember / LegacyMember origin buckets are left
 * as-is. A converted prospect is split into Current vs Previous by whether their
 * membership is actually active, because LeadType stays "Membership" even after
 * a member lapses (it is never auto-reverted), so LeadType alone can't tell a
 * current member from a lapsed one.
 *
 * Dead leads are the exception: a LeadType of "DeadLead" takes precedence over
 * LeadSource so that abandoned leads collect in their own tab regardless of how
 * they originated.
 */
export function classifyRetentionTab(member: MemberProfile): RetentionTab {
  const leadSource = normaliseMetadata(member.metadata?.LeadSource);
  const leadType = normaliseMetadata(member.metadata?.LeadType);
  const isMember = leadType === "Membership";

  if (leadType === "DeadLead") {
    return "deadleads";
  }

  const isProspectOrigin =
    leadSource === "Visitor" ||
    leadSource === "Tour" ||
    leadSource === "Call" ||
    leadSource === "";
  if (isMember && isProspectOrigin) {
    return getMemberStatus(member) === "Active" ? "current" : "previous";
  }

  switch (leadSource) {
    case "Visitor":
      return "visitors";
    case "Tour":
      return "tours";
    case "Call":
      return "calls";
    case "CurrentMember":
      return "current";
    case "PreviousMember":
      return "previous";
    case "LegacyMember":
      return "legacy";
    default:
      return "other";
  }
}

/**
 * Choose primary follow-up: nearest future nextFollowUpDate (or followUpDate), or latest if none future.
 */
function choosePrimaryFollowup(followups: MemberFollowup[]): MemberFollowup | null {
  if (followups.length === 0) return null;
  const today = dayjs().startOf("day");
  const withDates = followups
    .filter((f) => f.nextFollowUpDate || f.followUpDate)
    .map((f) => ({
      f,
      d: dayjs(f.nextFollowUpDate ?? f.followUpDate).startOf("day"),
    }));
  const future = withDates.filter(({ d }) => d.isSameOrAfter(today));
  if (future.length > 0) {
    future.sort((a, b) => a.d.valueOf() - b.d.valueOf());
    return future[0].f;
  }
  withDates.sort((a, b) => b.d.valueOf() - a.d.valueOf());
  return withDates[0]?.f ?? null;
}

/**
 * Join members with their follow-ups and attach primary follow-up for table display.
 * Members with at least one follow-up are included; optionally include members with no follow-up (empty primary).
 */
export function buildMembersWithPrimaryFollowup(
  members: MemberProfile[],
  followups: MemberFollowup[],
  options: { includeMembersWithNoFollowup?: boolean } = {}
): MemberWithPrimaryFollowup[] {
  const { includeMembersWithNoFollowup = true } = options;
  const byMembershipId = new Map<string, MemberFollowup[]>();
  for (const f of followups) {
    const id = f.membershipId;
    if (!byMembershipId.has(id)) byMembershipId.set(id, []);
    byMembershipId.get(id)!.push(f);
  }

  const result: MemberWithPrimaryFollowup[] = [];
  for (const member of members) {
    const list = byMembershipId.get(member.membershipId) ?? [];
    const primary = choosePrimaryFollowup(list);
    if (!includeMembersWithNoFollowup && list.length === 0) continue;
    result.push({ member, primaryFollowup: primary, allFollowups: list });
  }
  return result;
}

/**
 * The date a lead is next due: its next follow-up, or the last contact date when no next
 * date was set. The CRM's ordering, its overdue/due-today row colours and its KPI counts
 * all key off this one value.
 */
export function nextDueDate(row: MemberWithPrimaryFollowup): string | Date | null {
  // Not narrowed to `string`: DateReviver turns any ISO-looking response field into a Date,
  // so a follow-up that hasn't been through normalizeFollowupFromApi holds one. Declaring
  // string here would let a caller do string ops on a Date with a green type-check.
  return row.primaryFollowup?.nextFollowUpDate ?? row.primaryFollowup?.followUpDate ?? null;
}

/**
 * Soonest-due first, undated leads last.
 *
 * This ordering used to come from a default sort on the Next Follow-Up grid column. That
 * column moved into the lead detail panel when the table was trimmed to the eight columns
 * worth scanning, so the order is applied to the rows instead — otherwise dropping a column
 * would silently have reshuffled the page. Sorting a column in the grid still overrides it.
 */
export function byNextDueDate(
  a: MemberWithPrimaryFollowup,
  b: MemberWithPrimaryFollowup
): number {
  const rawA = nextDueDate(a)?.toString().trim() ?? "";
  const rawB = nextDueDate(b)?.toString().trim() ?? "";
  // toString() rather than a cast: dayjs parses both the ISO string the API sends and the
  // "Mon Aug 10 2026 …" form a revived Date stringifies to.
  if (!rawA && !rawB) return 0;
  if (!rawA) return 1;
  if (!rawB) return -1;
  const dA = dayjs(rawA);
  const dB = dayjs(rawB);
  if (!dA.isValid() && !dB.isValid()) return 0;
  if (!dA.isValid()) return 1;
  if (!dB.isValid()) return -1;
  return dA.valueOf() - dB.valueOf();
}
